import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import useContact from '../hooks/useContact';

export default function Footer() {
  const { t } = useLanguage();
  const { phone, phone2, email, address, hours, whatsappLink, facebookUrl } = useContact();
  const f = t.footer;

  return (
    <footer style={{ backgroundColor: '#0A3F22', color: '#FFFFFF' }}>
      {/* Main Footer */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/2a6bfe0d-bf62-4b21-995b-6c2794398f74/images/5366a31807515758e52f2da5be6a24582972673174d92b993737d70b7babc123.png"
                alt="Shree Ram Agro Industries Logo"
                className="w-12 h-12 object-contain"
              />
              <div>
                <div className="font-heading font-bold text-sm text-white leading-none">SHREE RAM</div>
                <div className="font-heading font-semibold text-xs leading-none mt-0.5" style={{ color: '#FCD34D' }}>AGRO INDUSTRIES</div>
              </div>
            </div>
            <p className="text-sm mb-4 leading-relaxed" style={{ color: '#A4C8AF' }}>
              {f.about}
            </p>
            <p className="text-xs italic" style={{ color: '#7AB08C' }}>{f.tagline}</p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading font-semibold text-white mb-4 text-sm uppercase tracking-wider">{f.quickLinks}</h3>
            <ul className="space-y-2">
              {[
                { to: '/products', label: f.links.products },
                { to: '/about', label: f.links.about },
                { to: '/dealers', label: f.links.dealers },
                { to: '/gallery', label: f.links.gallery },
                { to: '/contact', label: f.links.contact },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm transition-colors duration-200 hover:text-white"
                    style={{ color: '#A4C8AF' }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h3 className="font-heading font-semibold text-white mb-4 text-sm uppercase tracking-wider">{f.products}</h3>
            <ul className="space-y-2">
              {[
                { name: 'Rotavator', id: 'rotavator' },
                { name: 'Mini Rotavator', id: 'mini-rotavator' },
                { name: 'Disk Harrow', id: 'disk-harrow' },
                { name: 'Mulcher', id: 'mulcher' },
                { name: 'Fertilizer Spreader', id: 'fertilizer-spreader' },
                { name: 'Haloom Mulcher (Potato Mulcher)', id: 'potato-mulcher' },
                { name: 'Spring Tine Cultivator', id: 'cultivator' },
              ].map((p) => (
                <li key={p.id}>
                  <Link to={`/products#${p.id}`} className="text-sm transition-colors duration-200 hover:text-white" style={{ color: '#A4C8AF' }}>
                    {p.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-heading font-semibold text-white mb-4 text-sm uppercase tracking-wider">{f.contactInfo}</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <MapPin size={14} className="mt-0.5 flex-shrink-0" style={{ color: '#D97706' }} />
                <span className="text-sm" style={{ color: '#A4C8AF' }}>{address}</span>
              </li>
              <li className="flex items-center gap-2">
                <Phone size={14} className="flex-shrink-0" style={{ color: '#D97706' }} />
                <a href={`tel:${phone}`} className="text-sm transition-colors hover:text-white" style={{ color: '#A4C8AF' }}>{phone}</a>
              </li>
              <li className="flex items-center gap-2">
                <Phone size={14} className="flex-shrink-0" style={{ color: '#D97706' }} />
                <a href={`tel:${phone2}`} className="text-sm transition-colors hover:text-white" style={{ color: '#A4C8AF' }}>{phone2}</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={14} className="flex-shrink-0" style={{ color: '#D97706' }} />
                <a href={`mailto:${email}`} className="text-sm transition-colors hover:text-white" style={{ color: '#A4C8AF' }}>{email}</a>
              </li>
              <li className="flex items-start gap-2">
                <Clock size={14} className="mt-0.5 flex-shrink-0" style={{ color: '#D97706' }} />
                <span className="text-sm" style={{ color: '#A4C8AF' }}>{hours}</span>
              </li>
            </ul>
            {/* WhatsApp CTA */}
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp"
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded text-white text-sm font-semibold transition-opacity hover:opacity-90"
              style={{ backgroundColor: '#25D366' }}
            >
              <svg viewBox="0 0 32 32" width="16" height="16" fill="white">
                <path d="M16.004 2.667C8.636 2.667 2.667 8.636 2.667 16c0 2.347.618 4.572 1.7 6.512L2.667 29.333l6.987-1.671A13.28 13.28 0 0016.004 29.333c7.364 0 13.33-5.969 13.33-13.333S23.368 2.667 16.004 2.667z"/>
              </svg>
              WhatsApp Us
            </a>
            {/* Facebook CTA */}
            <a
              href={facebookUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook"
              className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded text-white text-sm font-semibold transition-opacity hover:opacity-90"
              style={{ backgroundColor: '#1877F2' }}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="white">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              5K+ Followers
            </a>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t" style={{ borderColor: '#0D4D2B' }}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 flex flex-col md:flex-row items-center justify-between gap-2">
          <p className="text-xs" style={{ color: '#7AB08C' }}>{f.copyright}</p>
          <p className="text-xs" style={{ color: '#7AB08C' }}>Powered by Indian Engineering</p>
        </div>
      </div>
    </footer>
  );
}
